import { useEffect } from 'react';
import type { ShogunChopDaemonNotice } from '@localagent/sdk';
import { useShogunchop } from '../../providers/ShogunChopProvider';
import { toast } from '@localagent/ui/components/sonner';
import { getSDK, getDeviceIdAsync } from '../../services/sdk-instance';
import { mcpCommandErrorMessage } from '../../hooks/tools-servers';
import { showDaemonNoticeToast, shouldShowDaemonNotice } from './daemon-notices';

/**
 * Desktop-only subscriber for daemon-originated notices (shogunchop ADR-d2bfe).
 *
 * Mounted app-wide beside `NotificationToastDispatcher`, and deliberately NOT
 * merged with it: that pipeline is backend-originated and rides the cloud
 * bridge, while this rides the daemon's authenticated loopback `/events`.
 * A web tab has no daemon, so the effect returns before opening anything.
 */
export function DaemonNoticeDispatcher(): null {
  const shogunchop = useShogunchop();

  useEffect(() => {
    const sc = shogunchop.resource;
    if (!sc) return;

    const relay = sc.sharedEvents();

    relay.on((event) => {
      if (event.event !== 'daemon:notice') return;
      const notice = event.data as ShogunChopDaemonNotice;
      if (!shouldShowDaemonNotice(notice)) return;
      const server = notice.server;
      showDaemonNoticeToast(
        notice,
        server
          ? () => {
              void (async () => {
                try {
                  await getSDK().tools.controlMCP(await getDeviceIdAsync(), server.mcp_id, 'restart');
                  toast.success(`${server.name} restarted`);
                } catch (error) {
                  toast.error(mcpCommandErrorMessage(error as Error, true));
                }
              })();
            }
          : undefined,
      );
    });

    return () => {
      relay.release();
    };
  }, [shogunchop.resource]);

  return null;
}
